import { addDays, dayOfWeek, daysBetween } from '../../lib/dates/iso.ts';
import { formatDay } from '../../lib/format/dates.ts';
import type { WeekView } from '../api.ts';
import { copy } from '../copy/en-CA.ts';

const LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

interface Cell {
  key: string;
  kind: 'wd' | 'we' | 'hol' | 'ghost';
  label: string;
  title: string;
}

/**
 * The seven (or eight, or six) days of a week as small cells: weekdays
 * light, the weekend after them set apart, a holiday outlined, an extra
 * Monday on an 8-day week, and a bold X over the Monday a 6-day week does
 * not include (approved design, docs/plan.md §9).
 */
function cellsFor(week: WeekView): Cell[] {
  const cells: Cell[] = [];
  const holidayDates = new Set<string>();
  if (week.coveredMonday) holidayDates.add(week.coveredMonday.date);
  // A Friday holiday (Good Friday, Canada Day on a Friday) sits inside the week.
  if (week.longWeekendHolidays.length > 0 && !week.coveredMonday) {
    for (let d = week.onCallStart; d <= week.onCallEnd; d = addDays(d, 1)) if (dayOfWeek(d) === 5) holidayDates.add(d);
  }
  if (week.kind === 'christmas' || week.kind === 'new-year') {
    for (let d = week.onCallStart; d <= week.onCallEnd; d = addDays(d, 1)) {
      const md = d.slice(5);
      if (md === '12-25' || md === '12-26' || md === '01-01') holidayDates.add(d);
    }
  }
  if (week.skippedMonday) {
    cells.push({ key: week.skippedMonday.date, kind: 'ghost', label: 'M', title: `${formatDay(week.skippedMonday.date)}: ${copy.schedule.legend.covered}` });
  }
  const n = daysBetween(week.onCallStart, week.onCallEnd) + 1;
  for (let i = 0; i < n; i++) {
    const d = addDays(week.onCallStart, i);
    const dow = dayOfWeek(d);
    const isHoliday = holidayDates.has(d);
    const kind: Cell['kind'] = isHoliday ? 'hol' : dow === 0 || dow === 6 ? 'we' : 'wd';
    const title = isHoliday ? `${formatDay(d)}: ${copy.schedule.legend.holiday}` : formatDay(d);
    cells.push({ key: d, kind, label: LETTERS[dow] ?? '', title });
  }
  return cells;
}

export function DayStrip({ week }: { week: WeekView }) {
  const cells = cellsFor(week);
  return (
    <div className="strip" aria-hidden="true">
      {cells.map((c, i) => {
        const nextIsWeekend = cells[i + 1]?.kind === 'we' && c.kind !== 'we' && c.kind !== 'ghost';
        return (
          <span key={c.key} className={`cell ${c.kind}${nextIsWeekend ? ' before-weekend' : ''}`} title={c.title}>
            {c.label}
            {c.kind === 'ghost' ? (
              <svg className="x" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round">
                <path d="M4 4l16 16M20 4L4 20" />
              </svg>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}
